import { getPartnerInsertRows, partnerDuplicateKeys, partnerLookupIndexes, partnerName, validatePartnerRow } from "./partnerImportModel";

export const partnerRecordKey = record => `${record?.ExcelRow}:${record?.LedgerID}`;
export const isPartnerRoleLocked = (row, id) => ["Saved", "Saving", "Uncertain"].includes(row.saveResults?.[id]?.Status);
export const isPartnerRowUncertain = row => Object.values(row.saveResults || {}).some(result => result.Status === "Uncertain");
export const hasPartnerSavedRole = row => Object.values(row.saveResults || {}).some(result => result.Status === "Saved");
export const clearPartnerEditableResults = row => Object.fromEntries(Object.entries(row.saveResults || {}).filter(([, result]) => ["Saved", "Saving", "Uncertain"].includes(result.Status)));

export function applyPartnerSaveResults(rows, records) {
  const updates = new Map();
  records.forEach(record => {
    if (!updates.has(record.ExcelRow)) updates.set(record.ExcelRow, []);
    updates.get(record.ExcelRow).push(record);
  });
  return rows.map(row => {
    const changed = updates.get(row.excelRow);
    if (!changed) return row;
    const saveResults = { ...row.saveResults };
    changed.forEach(record => { saveResults[record.LedgerID] = { ...record, payload: ["Saving", "Uncertain"].includes(record.Status) ? record.payload || saveResults[record.LedgerID]?.payload : undefined }; });
    return { ...row, saveResults };
  });
}

export function partnerSaveCounts(rows) {
  const counts = { Saved: 0, Exists: 0, Failed: 0, Uncertain: 0, Saving: 0, Pending: 0 };
  rows.forEach(row => (row.ledgerIds || []).forEach(id => { counts[row.saveResults?.[id]?.Status || "Pending"]++; }));
  return counts;
}

function verifyResponse(response, payloads) {
  const expected = new Set(payloads.map(partnerRecordKey));
  if (!Array.isArray(response?.Rows) || response.Rows.length !== expected.size ||
    new Set(response.Rows.map(partnerRecordKey)).size !== expected.size || response.Rows.some(record =>
      !expected.has(partnerRecordKey(record)) || !["Saved", "Exists", "Failed"].includes(record.Status) ||
      !Array.isArray(record.Issues) || record.Issues.some(issue => typeof issue?.Message !== "string" || !issue.Message.trim() ||
        typeof issue.Field !== "string" || !["Error", "Info"].includes(issue.Severity)) ||
      (["Saved", "Exists"].includes(record.Status) && (!Number.isInteger(record.AccountID) || record.AccountID <= 0)) ||
      (["Failed", "Exists"].includes(record.Status) && !record.Issues.length)))
    throw new Error("Përgjigjja e ruajtjes nuk u verifikua. Regjistrimet e kësaj pjese mbeten të pakonfirmuara.");
}

export async function savePartnerImportBatches(rows, lookups, { save, retryUncertain = false,
  signal, shouldStop, onBatchStart, onBatchResult, onBatchUncertain, onProgress } = {}) {
  const indexes = partnerLookupIndexes(lookups);
  const duplicates = partnerDuplicateKeys(rows);
  const candidates = [];
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    if (signal?.aborted) throw new Error("Ruajtja u ndërpre.");
    const source = rows[i];
    const row = validatePartnerRow(source, indexes);
    const localInvalid = Object.keys(row.errors).length > 0;
    const ledgers = (row.ledgerIds || []).filter(id => {
      const status = source.saveResults?.[id]?.Status;
      if (["Saved", "Exists"].includes(status)) return false;
      if (status === "Uncertain") return retryUncertain;
      return !localInvalid && duplicates.get(`${id}:${partnerName(row.identification)}`) <= 1;
    });
    if (ledgers.length) { candidates.push({ ...row, ledgerIds: ledgers }); total += ledgers.length; }
    if (i % 5000 === 4999) await new Promise(resolve => setTimeout(resolve, 0));
  }
  onProgress?.({ completed: 0, total });
  let batch = [], completed = 0, estimatedBytes = 32;
  const maxEstimatedBytes = 3 * 1024 * 1024;
  const send = async () => {
    if (signal?.aborted) throw new Error("Ruajtja u ndërpre.");
    const payloads = batch;
    onBatchStart?.(payloads.map(payload => ({ ExcelRow: payload.ExcelRow, LedgerID: payload.LedgerID,
      Status: "Saving", Issues: [], payload })));
    try {
      const response = await save({ Rows: payloads }, signal);
      verifyResponse(response, payloads);
      onBatchResult?.(response.Rows);
    } catch (error) {
      onBatchUncertain?.(payloads.map(payload => ({ ExcelRow: payload.ExcelRow, LedgerID: payload.LedgerID, Status: "Uncertain", Issues: [], payload })));
      throw error;
    }
    completed += payloads.length;
    onProgress?.({ completed, total });
    batch = []; estimatedBytes = 32;
    await new Promise(resolve => setTimeout(resolve, 0));
  };
  for (const row of candidates) {
    if (shouldStop?.()) return { completed, total, stopped: true };
    for (const record of getPartnerInsertRows([row])) {
      const prior = row.saveResults?.[record.LedgerID];
      const payload = prior?.Status === "Uncertain" ? prior.payload : record;
      if (!payload) throw new Error("Mungojnë të dhënat e tentativës së mëparshme. Kontrolloni regjistrimet në databazë.");
      // UTF-8 uses at most three bytes per UTF-16 code unit. Leave room below the endpoint's 4 MB limit.
      const bytes = JSON.stringify(payload).length * 3 + 3;
      if (bytes + 32 > maxEstimatedBytes) {
        onBatchResult?.([{ ExcelRow: record.ExcelRow, LedgerID: record.LedgerID, Status: "Failed", Issues: [{
          Field: "save", Code: "PayloadTooLarge", Severity: "Error", Message: "Rreshti është shumë i madh për ruajtje. Zvogëloni tekstin në fushat e gjata dhe riprovoni."
        }] }]);
        completed++; onProgress?.({ completed, total });
        continue;
      }
      if (estimatedBytes + bytes > maxEstimatedBytes && batch.length) {
        await send();
        if (shouldStop?.()) return { completed, total, stopped: true };
      }
      batch.push(payload); estimatedBytes += bytes;
      if (batch.length === 500) {
        await send();
        if (shouldStop?.()) return { completed, total, stopped: true };
      }
    }
  }
  if (batch.length && !shouldStop?.()) await send();
  return { completed, total, stopped: Boolean(shouldStop?.()) };
}
